import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { customerApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { ConsentCheckboxes } from '@/components/customer/ConsentCheckboxes';
import { ChevronLeft, Check } from 'lucide-react';

const STEP_LABELS = ['Your Details', 'Choose Plan', 'Review & Pay'];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', password: '', confirmPassword: '' });
  const [plans, setPlans] = useState([]);
  const [items, setItems] = useState([]);
  const [planId, setPlanId] = useState('');
  const [itemIds, setItemIds] = useState([]);
  const [discountInput, setDiscountInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('IN_STORE');
  const [allergyOk, setAllergyOk] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    customerApi.get('/registration/options').then(({ data }) => {
      setPlans(data.plans);
      setItems(data.items);
      if (data.plans.length) setPlanId(data.plans[0].id);
    });
  }, []);

  useEffect(() => {
    if (!planId) return;
    setQuoteLoading(true);
    const t = setTimeout(() => {
      customerApi
        .post('/registration/quote', { planId, itemIds, discountCode: appliedDiscount || undefined })
        .then(({ data }) => setQuote(data))
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [planId, itemIds, appliedDiscount]);

  const toggleItem = (id) => setItemIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const step1Valid =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.phone.trim() &&
    form.email.trim() &&
    form.password.length >= 6 &&
    form.password === form.confirmPassword;

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const data = await register({
        name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        planId,
        itemIds,
        discountCode: appliedDiscount || undefined,
        paymentMethod,
        acceptedAllergy: allergyOk,
        acceptedTerms: termsOk,
      });
      if (data.payment?.source === 'ONLINE') navigate(`/pay/${data.payment.id}`);
      else navigate('/account');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/logo-wordmark.png"
            alt="The Tiffin by The Virsa"
            className="mx-auto h-16 sm:h-20 object-contain"
          />
          <p className="text-muted-ink text-sm mt-1">Daily Fresh. Daily Different.</p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-6" data-testid="register-step-indicator">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors duration-150 ${
                  i < step ? 'bg-coriander text-white' : i === step ? 'bg-saffron text-white' : 'bg-white text-muted-ink border border-border/40'
                }`}
              >
                {i < step ? <Check size={13} /> : i + 1}
              </div>
              {i < STEP_LABELS.length - 1 && <div className="w-8 h-px bg-border/40" />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 space-y-4 text-charcoal">
          {step === 0 && (
            <>
              <h2 className="font-heading text-xl font-semibold text-charcoal">{STEP_LABELS[0]}</h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">First name</Label>
                  <Input id="firstName" required data-testid="register-firstname-input" value={form.firstName} onChange={update('firstName')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input id="lastName" required data-testid="register-lastname-input" value={form.lastName} onChange={update('lastName')} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone number</Label>
                <Input id="phone" required data-testid="register-phone-input" value={form.phone} onChange={update('phone')} placeholder="780 555 0100" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required data-testid="register-email-input" value={form.email} onChange={update('email')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <PasswordInput id="password" required minLength={6} data-testid="register-password-input" toggleTestId="register-password-toggle" value={form.password} onChange={update('password')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <PasswordInput
                  id="confirmPassword"
                  required
                  data-testid="register-confirm-password-input"
                  toggleTestId="register-confirm-password-toggle"
                  value={form.confirmPassword}
                  onChange={update('confirmPassword')}
                />
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>
              <Button
                data-testid="register-step1-continue-btn"
                disabled={!step1Valid}
                onClick={() => setStep(1)}
                className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
              >
                Continue
              </Button>
              <p className="text-sm text-center pt-1">
                Already have an account?{' '}
                <a href="/cxlogin" data-testid="register-login-link" className="text-coriander hover:underline">
                  Log in
                </a>
              </p>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="font-heading text-xl font-semibold text-charcoal">Choose Your Plan</h2>
              <RadioGroup value={planId} onValueChange={setPlanId} className="space-y-2">
                {plans.map((p) => (
                  <label
                    key={p.id}
                    data-testid={`plan-option-${p.id}`}
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${
                      planId === p.id ? 'border-saffron bg-saffron/5' : 'border-border/40'
                    }`}
                  >
                    <RadioGroupItem value={p.id} data-testid={`plan-radio-${p.id}`} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between">
                        <p className="font-heading font-semibold text-charcoal">{p.name}</p>
                        <p className="font-heading font-700 text-charcoal">${Number(p.price).toFixed(2)}</p>
                      </div>
                      <p className="text-xs text-muted-ink mt-0.5">{p.tiffinCount} tiffins · valid {p.validityDays} days</p>
                      {p.description && <p className="text-xs text-muted-ink mt-1">{p.description}</p>}
                    </div>
                  </label>
                ))}
                {plans.length === 0 && <p className="text-sm text-muted-ink">No plans available right now.</p>}
              </RadioGroup>

              {items.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm font-medium text-charcoal mb-2">Add-ons (optional) <span className="text-xs text-muted-ink font-normal">— priced per tiffin</span></p>
                  <div className="space-y-2">
                    {items.map((it) => (
                      <label
                        key={it.id}
                        data-testid={`addon-option-${it.id}`}
                        className="flex items-center gap-3 rounded-xl border border-border/40 p-3 cursor-pointer"
                      >
                        <Checkbox
                          data-testid={`addon-checkbox-${it.id}`}
                          checked={itemIds.includes(it.id)}
                          onCheckedChange={() => toggleItem(it.id)}
                        />
                        <div className="flex-1 flex items-baseline justify-between">
                          <p className="text-sm text-charcoal">{it.name}</p>
                          <p className="text-sm font-medium text-charcoal">${Number(it.price).toFixed(2)} <span className="text-muted-ink font-normal text-xs">/ tiffin</span></p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 h-11" data-testid="register-step2-back-btn" onClick={() => setStep(0)}>
                  <ChevronLeft size={16} className="mr-1" /> Back
                </Button>
                <Button
                  className="flex-1 h-11 bg-saffron hover:bg-saffron-hover"
                  data-testid="register-step2-continue-btn"
                  disabled={!planId}
                  onClick={() => setStep(2)}
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-heading text-xl font-semibold text-charcoal">Order Summary</h2>

              <div className="space-y-1.5">
                <Label htmlFor="discountCode">Discount Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="discountCode"
                    data-testid="discount-code-input"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value)}
                    placeholder="e.g. WELCOME10"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="apply-discount-btn"
                    onClick={() => setAppliedDiscount(discountInput.trim())}
                  >
                    Apply
                  </Button>
                </div>
                {quote?.discountError && appliedDiscount && (
                  <p className="text-xs text-destructive" data-testid="discount-error">{quote.discountError}</p>
                )}
                {quote && !quote.discountError && quote.discountAmount > 0 && (
                  <p className="text-xs text-coriander" data-testid="discount-applied-badge">
                    Discount applied: -${quote.discountAmount.toFixed(2)}
                  </p>
                )}
              </div>

              {quote && (
                <div
                  className={`rounded-xl bg-bone p-4 space-y-1.5 text-sm transition-opacity duration-150 ${quoteLoading ? 'opacity-50' : ''}`}
                  data-testid="order-summary-card"
                >                  <div className="flex justify-between">
                    <span className="text-muted-ink">Plan: {quote.plan.name}</span>
                    <span className="text-charcoal">${Number(quote.plan.price).toFixed(2)}</span>
                  </div>
                  {quote.items.map((it) => (
                    <div key={it.id} className="flex justify-between">
                      <span className="text-muted-ink">{it.name} <span className="text-xs">(${Number(it.unitPrice).toFixed(2)}/tiffin × {quote.plan.tiffinCount})</span></span>
                      <span className="text-charcoal">${Number(it.amount).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1 border-t border-border/40">
                    <span className="text-muted-ink">Subtotal</span>
                    <span className="text-charcoal" data-testid="order-summary-subtotal">${quote.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-ink">Discount</span>
                    <span className="text-charcoal" data-testid="order-summary-discount">-${quote.discountAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-border/40">
                    <span className="font-semibold text-charcoal">Final Total</span>
                    <span className="font-heading font-700 text-lg text-charcoal" data-testid="order-summary-total">
                      ${quote.finalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-charcoal mb-2">Payment Method</p>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
                  <label
                    data-testid="payment-method-online"
                    className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${
                      paymentMethod === 'ONLINE' ? 'border-saffron bg-saffron/5' : 'border-border/40'
                    }`}
                  >
                    <RadioGroupItem value="ONLINE" data-testid="payment-radio-online" />
                    <span className="text-sm text-charcoal">Pay Online</span>
                  </label>
                  <label
                    data-testid="payment-method-instore"
                    className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${
                      paymentMethod === 'IN_STORE' ? 'border-saffron bg-saffron/5' : 'border-border/40'
                    }`}
                  >
                    <RadioGroupItem value="IN_STORE" data-testid="payment-radio-instore" />
                    <span className="text-sm text-charcoal">Pay At Store</span>
                  </label>
                </RadioGroup>
                <p className="text-xs text-muted-ink mt-2">
                  Your subscription stays pending until payment is confirmed — visit the store to complete it and get activated.
                </p>
              </div>

              {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}

              <div className="border-t border-border/40 pt-3">
                <ConsentCheckboxes
                  allergyChecked={allergyOk}
                  termsChecked={termsOk}
                  onAllergyChange={setAllergyOk}
                  onTermsChange={setTermsOk}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-11" data-testid="register-step3-back-btn" onClick={() => setStep(1)}>
                  <ChevronLeft size={16} className="mr-1" /> Back
                </Button>
                <Button
                  className="flex-1 h-11 bg-saffron hover:bg-saffron-hover"
                  data-testid="register-submit-btn"
                  disabled={busy || quoteLoading || !allergyOk || !termsOk}
                  onClick={submit}
                >
                  {busy ? 'Creating account…' : 'Create Account'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
